import React, { useState } from 'react';
import {
  Search,
  Plus,
  MoreVertical,
  Mail,
  MessageSquare,
  FileText,
  Share2,
  Download,
  Edit,
  Trash2,
  User,
  Phone,
  X,
} from 'lucide-react';

interface Lead {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  source: 'facebook' | 'instagram' | 'website' | 'manual' | 'referral';
  status: 'new' | 'thinking' | 'enrolled' | 'disqualified';
  interestedCourses: string[];
  dateAdded: string;
  notes?: string;
}

const mockLeads: Lead[] = [
  {
    id: '1',
    firstName: 'Sarah',
    lastName: 'Cohen',
    email: 'sarah.cohen@email.com',
    phone: '+972-50-123-4567',
    source: 'facebook',
    status: 'new',
    interestedCourses: ['Mathematics Advanced', 'Physics'],
    dateAdded: '2024-02-15',
  },
  {
    id: '2',
    firstName: 'David',
    lastName: 'Levi',
    email: 'david.levi@email.com',
    phone: '+972-52-987-6543',
    source: 'website',
    status: 'thinking',
    interestedCourses: ['English Literature'],
    dateAdded: '2024-02-14',
  },
  {
    id: '3',
    firstName: 'Maya',
    lastName: 'Green',
    email: 'maya.green@email.com',
    phone: '+972-54-555-1234',
    source: 'instagram',
    status: 'enrolled',
    interestedCourses: ['Chemistry 101', 'Biology'],
    dateAdded: '2024-02-10',
  },
  {
    id: '4',
    firstName: 'Yossi',
    lastName: 'Brown',
    email: 'yossi.b@email.com',
    phone: '+972-50-111-2222',
    source: 'referral',
    status: 'disqualified',
    interestedCourses: ['Physics'],
    dateAdded: '2024-02-08',
  },
];

const sourceLabels: Record<Lead['source'], string> = {
  facebook: 'Facebook',
  instagram: 'Instagram',
  website: 'Website',
  manual: 'Manual',
  referral: 'Referral',
};

const sourceColors: Record<Lead['source'], string> = {
  facebook: 'bg-blue-100 text-blue-700',
  instagram: 'bg-pink-100 text-pink-700',
  website: 'bg-purple-100 text-purple-700',
  manual: 'bg-gray-100 text-gray-700',
  referral: 'bg-green-100 text-green-700',
};

const statusLabels: Record<Lead['status'], string> = {
  new: 'New',
  thinking: 'Thinking',
  enrolled: 'Enrolled',
  disqualified: 'Disqualified',
};

const statusColors: Record<Lead['status'], string> = {
  new: 'bg-teal-100 text-teal-700',
  thinking: 'bg-yellow-100 text-yellow-700',
  enrolled: 'bg-green-100 text-green-700',
  disqualified: 'bg-red-100 text-red-700',
};

export function LeadsTab() {
  const [leads, setLeads] = useState<Lead[]>(mockLeads);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sourceFilter, setSourceFilter] = useState<string>('all');
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [editingLead, setEditingLead] = useState<Lead | null>(null);
  const [contextMenuId, setContextMenuId] = useState<string | null>(null);

  const filteredLeads = leads.filter(lead => {
    const matchesSearch = `${lead.firstName} ${lead.lastName}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || lead.status === statusFilter;
    const matchesSource = sourceFilter === 'all' || lead.source === sourceFilter;
    return matchesSearch && matchesStatus && matchesSource;
  });

  const handleEditLead = (lead: Lead) => {
    setEditingLead(lead);
    setShowLeadModal(true);
    setContextMenuId(null);
  };

  const handleDeleteLead = (leadId: string) => {
    if (confirm('Are you sure you want to delete this lead?')) {
      setLeads(leads.filter(l => l.id !== leadId));
    }
    setContextMenuId(null);
  };

  const handleCreateAsStudent = (lead: Lead) => {
    alert(`Creating student account for ${lead.firstName} ${lead.lastName}`);
    setContextMenuId(null);
  };

  return (
    <div>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-semibold text-gray-900">Leads</h2>
          <span className="px-2 py-0.5 text-sm bg-gray-100 text-gray-600 rounded-full">
            {filteredLeads.length}
          </span>
        </div>

        <div className="flex items-center gap-3">
          <button className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2">
            <FileText className="w-4 h-4" />
            New Report
          </button>
          <button className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2">
            <Share2 className="w-4 h-4" />
            Share
          </button>
          <button
            onClick={() => {
              setEditingLead(null);
              setShowLeadModal(true);
            }}
            className="px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add New Lead
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        {/* Search */}
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search leads..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
          />
        </div>

        {/* Status Filter */}
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
        >
          <option value="all">All Status</option>
          <option value="new">New</option>
          <option value="thinking">Thinking</option>
          <option value="enrolled">Enrolled</option>
          <option value="disqualified">Disqualified</option>
        </select>

        {/* Source Filter */}
        <select
          value={sourceFilter}
          onChange={(e) => setSourceFilter(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
        >
          <option value="all">All Sources</option>
          <option value="facebook">Facebook</option>
          <option value="instagram">Instagram</option>
          <option value="website">Website</option>
          <option value="manual">Manual</option>
          <option value="referral">Referral</option>
        </select>
      </div>

      {/* Leads Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Lead
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Source
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date Added
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Interested In
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredLeads.map((lead) => (
                <tr key={lead.id} className="hover:bg-gray-50">
                  {/* Lead Name */}
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center text-sm font-medium text-teal-700">
                        {lead.firstName[0]}{lead.lastName[0]}
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">
                          {lead.firstName} {lead.lastName}
                        </div>
                        <div className="text-sm text-gray-500">{lead.email}</div>
                      </div>
                    </div>
                  </td>

                  {/* Source */}
                  <td className="px-4 py-4">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${sourceColors[lead.source]}`}>
                      {sourceLabels[lead.source]}
                    </span>
                  </td>

                  {/* Date Added */}
                  <td className="px-4 py-4">
                    <span className="text-sm text-gray-600">
                      {new Date(lead.dateAdded).toLocaleDateString()}
                    </span>
                  </td>

                  {/* Contact */}
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-2">
                      <button className="p-1.5 text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded">
                        <Phone className="w-4 h-4" />
                      </button>
                      <button className="p-1.5 text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded">
                        <Mail className="w-4 h-4" />
                      </button>
                      <button className="p-1.5 text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded">
                        <MessageSquare className="w-4 h-4" />
                      </button>
                    </div>
                  </td>

                  {/* Status */}
                  <td className="px-4 py-4">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusColors[lead.status]}`}>
                      {statusLabels[lead.status]}
                    </span>
                  </td>

                  {/* Interested In */}
                  <td className="px-4 py-4">
                    <div className="flex flex-wrap gap-1 max-w-[200px]">
                      {lead.interestedCourses.slice(0, 2).map((course, idx) => (
                        <span key={idx} className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">
                          {course}
                        </span>
                      ))}
                      {lead.interestedCourses.length > 2 && (
                        <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">
                          +{lead.interestedCourses.length - 2}
                        </span>
                      )}
                    </div>
                  </td>

                  {/* Actions */}
                  <td className="px-4 py-4">
                    <div className="relative">
                      <button
                        onClick={() => setContextMenuId(contextMenuId === lead.id ? null : lead.id)}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                      >
                        <MoreVertical className="w-4 h-4" />
                      </button>

                      {/* Context Menu */}
                      {contextMenuId === lead.id && (
                        <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                          <button
                            onClick={() => handleEditLead(lead)}
                            className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                          >
                            <Edit className="w-4 h-4" />
                            Edit Lead Details
                          </button>
                          <button
                            onClick={() => handleCreateAsStudent(lead)}
                            className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                          >
                            <User className="w-4 h-4" />
                            Create as Student
                          </button>
                          <button
                            onClick={() => setContextMenuId(null)}
                            className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                          >
                            <FileText className="w-4 h-4" />
                            Send Billing Form
                          </button>
                          <button
                            onClick={() => setContextMenuId(null)}
                            className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                          >
                            <Mail className="w-4 h-4" />
                            Send Info by Mail
                          </button>
                          <button
                            onClick={() => setContextMenuId(null)}
                            className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                          >
                            <MessageSquare className="w-4 h-4" />
                            Send WhatsApp
                          </button>
                          <hr className="my-1" />
                          <button
                            onClick={() => handleDeleteLead(lead.id)}
                            className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                            Archive Lead
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredLeads.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">No leads found</p>
          </div>
        )}
      </div>

      {/* Lead Modal */}
      {showLeadModal && (
        <LeadModal
          lead={editingLead}
          onClose={() => {
            setShowLeadModal(false);
            setEditingLead(null);
          }}
          onSave={(leadData) => {
            if (editingLead) {
              setLeads(leads.map(l => l.id === editingLead.id ? { ...l, ...leadData } : l));
            } else {
              const newLead: Lead = {
                ...leadData as Lead,
                id: Date.now().toString(),
                dateAdded: new Date().toISOString().split('T')[0],
              };
              setLeads([newLead, ...leads]);
            }
            setShowLeadModal(false);
            setEditingLead(null);
          }}
        />
      )}
    </div>
  );
}

interface LeadModalProps {
  lead?: Lead | null;
  onClose: () => void;
  onSave: (data: Partial<Lead>) => void;
}

function LeadModal({ lead, onClose, onSave }: LeadModalProps) {
  const [formData, setFormData] = useState({
    firstName: lead?.firstName || '',
    lastName: lead?.lastName || '',
    email: lead?.email || '',
    phone: lead?.phone || '',
    source: lead?.source || 'manual' as Lead['source'],
    status: lead?.status || 'new' as Lead['status'],
    interestedCourses: lead?.interestedCourses || [] as string[],
    notes: lead?.notes || '',
  });

  const [courseInput, setCourseInput] = useState('');

  const isEditMode = !!lead;

  const handleAddCourse = () => {
    if (courseInput.trim() && !formData.interestedCourses.includes(courseInput.trim())) {
      setFormData(prev => ({
        ...prev,
        interestedCourses: [...prev.interestedCourses, courseInput.trim()],
      }));
      setCourseInput('');
    }
  };

  const handleRemoveCourse = (course: string) => {
    setFormData(prev => ({
      ...prev,
      interestedCourses: prev.interestedCourses.filter(c => c !== course),
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4 bg-black/50">
        <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              {isEditMode ? 'Edit Lead' : 'Create New Lead'}
            </h2>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 max-h-[70vh] overflow-y-auto">
            <div className="space-y-4">
              {/* Name Fields */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                    required
                  />
                </div>
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mobile Number
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="+972-50-123-4567"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  required
                />
              </div>

              {/* Interested Courses */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Interested In (Courses)
                </label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={courseInput}
                    onChange={(e) => setCourseInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddCourse())}
                    placeholder="Type course name..."
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                  <button
                    type="button"
                    onClick={handleAddCourse}
                    className="px-4 py-2 text-sm font-medium text-teal-600 border border-teal-600 rounded-lg hover:bg-teal-50"
                  >
                    Add
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.interestedCourses.map((course, idx) => (
                    <span key={idx} className="flex items-center gap-1 px-3 py-1 bg-teal-100 text-teal-700 rounded-full text-sm">
                      {course}
                      <button type="button" onClick={() => handleRemoveCourse(course)}>
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              {/* Source */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Source
                </label>
                <select
                  value={formData.source}
                  onChange={(e) => setFormData(prev => ({ ...prev, source: e.target.value as Lead['source'] }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option value="manual">Manual</option>
                  <option value="facebook">Facebook</option>
                  <option value="instagram">Instagram</option>
                  <option value="website">Website</option>
                  <option value="referral">Referral</option>
                </select>
              </div>

              {/* Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Lead Status
                </label>
                <div className="flex gap-2">
                  {(['new', 'thinking', 'enrolled', 'disqualified'] as Lead['status'][]).map((status) => (
                    <button
                      key={status}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, status }))}
                      className={`px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors ${
                        formData.status === status
                          ? statusColors[status].replace('bg-', 'bg-').replace('text-', 'text-') + ' border-current'
                          : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {statusLabels[status]}
                    </button>
                  ))}
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between mt-6 pt-6 border-t border-gray-200">
              {isEditMode && (
                <button
                  type="button"
                  className="px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg"
                >
                  Delete Lead
                </button>
              )}
              <div className={`flex items-center gap-3 ${!isEditMode ? 'ml-auto' : ''}`}>
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700"
                >
                  {isEditMode ? 'Save Changes' : 'Save New Lead'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
